import { Reducer } from 'redux';
import { Effect } from 'dva';
import { routerRedux } from 'dva/router';
import { stringify } from 'querystring';
import { notification } from 'antd';
import { getToken, setToken, setAuthority } from '@/utils/authority';
import { reloadAuthorized } from '@/utils/Authorized'


import { utilSetLocalStorage, utilClearLocalStorage } from '@/utils/storage_utils';

import {
  queryCurrent,
  signup as requestSignUp,
  signin as requestSignIn,
  logoutFromServer,
  requestResetPassword,
  requestconfirmResetPassword,
  getfetchsubscriptionStatus,
  updateSubscriptionStatus,
  getNotificationCounts,
  socialLogin,
  microsoftEmailFirstStart,
  microsoftEmailFirstComplete,
  validateInvitation as requestValidateInvitation,
  ssoEmailFirstStart,
  ssoEmailFirstComplete,
} from '@/services/user';
import { identifyUser, resetPosthog } from '@/utils/posthog';
import {
  captureUserSignedUp,
  captureUserLoggedIn,
  captureUserLoggedOut,
  captureUserLoginFailed,
} from '@/utils/posthogEvents';

import { getPageQuery } from '@/utils/utils';
import {
  CartListEP,
  PaymentBundleParams,
  SEOAllMapParams,
  SEOProfileParams,
} from '@/components/data';
import {
  listBundles,
  listCart,
  paymentUserSubscription,
  paymentConfig,
} from '@/components/Pricing/service';
import { getList, getProfile, getAllMap } from '@/pages/Public/service'; // SEO Service
import { cartManager, bundlesManager, packageManager } from '@/components/Pricing/aggregator';

export interface CurrentUser {
  id?: number | string;
  name?: string;
  email?: string;
  company?: string;
  avatar?: string;
  user_type?: number | string;
  geo?: string;
  designation?: string;
  plan?: number | string;
}

type UserAnalyticsPayload = {
  geo?: string | null;
  designation?: string | null;
  plan?: number | string | null;
};

const normalizeAnalyticsValue = (value?: string | number | null): string | number => {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string' && value.trim() === '') return 'none';
  return value;
};

const buildAnalyticsUser = (payload?: { user?: CurrentUser } & Record<string, any>): CurrentUser => {
  const user = payload?.user || {};
  const userAnalytics: UserAnalyticsPayload = payload?.user_analytics || {};

  return {
    ...user,
    geo: normalizeAnalyticsValue(userAnalytics.geo) as string,
    designation: normalizeAnalyticsValue(userAnalytics.designation) as string,
    plan: normalizeAnalyticsValue(userAnalytics.plan),
  };
};

export interface IFormErrors {
  [key: string]: string[];
}

export interface SubscriptionItem {
  subscription: string;
  name: string;
  email_subscribed: boolean;
  in_app_subscribed: boolean;
  path?: string;
  updating?: boolean;
}

export interface UserModelState {
  status?: 'ok' | 'error';
  resetPasswordStatus: boolean;
  token?: string | null;
  currentUser?: CurrentUser;
  signInErrors: IFormErrors;
  signUpErrors: IFormErrors;
  passwordResetErrors: IFormErrors;
  cartList: CartListEP;
  bundleList: PaymentBundleParams[];
  package: any;
  public_key: string;
  SEOList: any;
  SEOAllMap: SEOAllMapParams;
  SEOProfile: SEOProfileParams;
  subscriptions?: SubscriptionItem[];
  subsLoading?: boolean;
  notificationCount: {
    my_hub_unread_notification_count: number;
    unread_notification_count: number;
  };

}

// Resolve the post-authentication destination from ?redirect=.
// Returns a same-origin path, or null when there is nothing safe to honor:
// - a relative value no longer throws (it resolves against our own origin)
// - a cross-origin value is dropped instead of being followed
const resolveRedirect = (): string | null => {
  const { redirect } = getPageQuery() as { redirect?: string };
  if (!redirect) {
    return null;
  }
  let target: URL;
  try {
    target = new URL(redirect, window.location.origin);
  } catch (err) {
    return null;
  }
  if (target.origin !== window.location.origin) {
    return null;
  }
  let path = target.href.substr(target.origin.length);
  if (path.match(/^\/.*#/)) {
    path = path.substr(path.indexOf('#') + 1);
  }
  return path;
};

export interface UserModelType {
  namespace: 'user';
  state: UserModelState;
  effects: {
    signUp: Effect;
    signIn: Effect;
    fetchCurrent: Effect;
    logout: Effect;
    startResetPassword: Effect;
    resetPassword: Effect;
    confirmresetPassword: Effect;
    upgrade: Effect;
    listCart: Effect;
    listBundles: Effect;
    paymentBundle: Effect;
    paymentConfig: Effect;
    // SEO
    getList: Effect;
    getProfie: Effect;
    getAllMap: Effect;
    fetchsubscriptionStatus: Effect;
    updateSubscriptionStatus: Effect;
    fetchSubscriptions: Effect;
    updateSubscription: Effect;
    fetchNotificationCount: Effect;
    socialSignIn: Effect;
    validateInvitation: Effect;
    msEmailFirstStart: Effect;
    socialSignInGlobal: Effect
    ssoEmailFirstStart: Effect

  };
  reducers: {
    saveCurrentUser: Reducer<UserModelState>;
    saveStaus: Reducer<UserModelState>;
    savePasswordResetStatus: Reducer<UserModelState>;
    clearState: Reducer<UserModelState>;
    setSignInErrors: Reducer<UserModelState>;
    setSignUpErrors: Reducer<UserModelState>;
    setPasswordResetErrors: Reducer<UserModelState>;
    upgradeNotif: Reducer<UserModelState>;
    cartItems: Reducer<UserModelState>;
    bundleItems: Reducer<UserModelState>;
    userPackage: Reducer<UserModelState>;
    getPaymentKey: Reducer<UserModelState>;
    // SEO
    saveList: Reducer<UserModelState>;
    saveProfile: Reducer<UserModelState>;
    saveAllMap: Reducer<UserModelState>;
    savefetchsubscriptionStatus: Reducer<UserModelState>;
    setSubsLoading: Reducer<UserModelState>;
    saveSubscriptions: Reducer<UserModelState>;
    setSubscriptionUpdating: Reducer<UserModelState>;
    updateSingleSubscription: Reducer<UserModelState>;
  };
}

const UserModel: UserModelType = {
  namespace: 'user',

  state: {
    status: 'ok',
    resetPasswordStatus: false,
    token: getToken(),
    currentUser: {},
    signInErrors: {},
    signUpErrors: {},
    passwordResetErrors: {},
    cartList: {
      checkout_status: false,
      cart_list: [],
    },
    bundleList: [],
    package: {},
    public_key: '',
    SEOList: {},
    SEOProfile: {
      dbid: 0,
      name: '',
      first_name: '',
      last_name: '',
      middle_name: '',
      duty_station: '',
      organization: '',
      phone_number: '',
      designation: '',
      total_483_issued: 0,
      total_warning_letters_issued: 0,
      longest_inspection: 0,
      shortest_inspection: 0,
      total_number_of_inspections: 0,
      average_length_of_inspection: 0,
      active_locations: [],
      inspection_yearwise_count: {},
      slug_name: '',
    },
    SEOAllMap: {
      categories: {},
      countries: {},
      all_investigators: {},
    },
    subscriptions: [],
    subsLoading: false,
    notificationCount: {
      my_hub_unread_notification_count: 0,
      unread_notification_count: 0,
    },
  },

  effects: {
    *signUp({ payload }, { call, put }) {
      let response = yield call(requestSignUp, payload);
      if (!response.ok) {
        const msg =
          Array.isArray(response.message)
            ? response.message.join('\n')
            : response.message || 'Signup failed';

        yield put({
          type: 'setSignUpErrors',
          payload: {
            non_field_errors: [msg],
          },
        });
        return;
      }
      response = response.data

      yield put({
        type: 'saveCurrentUser',
        payload: response,
      });

      if (response.token) {
        setToken(response.token);
        // Identify as soon as signup authenticates the user so post-login
        // events are attributed correctly even before the main layout mounts.
        identifyUser(buildAnalyticsUser(response));
        captureUserSignedUp({ method: 'email', email_domain: response.user?.email?.split('@')[1] || response.email?.split('@')[1] || '' });

        const redirect = resolveRedirect();

        reloadAuthorized();
        yield put(routerRedux.replace(redirect || '/user/login'));
      }
    },
    *signIn({ payload }, { call, put }) {
      const response = yield call(requestSignIn, payload);
      if (response === {} || !response.user) {
        let res = {};
        try {
          res = yield response.json();
        } catch (err) {
          res = { non_field_errors: ['ERROR', 'Enter the correct login credentials!'] };
        }
        captureUserLoginFailed({ method: 'email' });
        yield put({
          type: 'setSignInErrors',
          payload: res,
        });
      } else {
        yield put({
          type: 'saveCurrentUser',
          payload: response,
        });
      }
      // Login successfully
      if (response.token) {
        setToken(response.token);
        // Identify directly in the auth success path rather than relying only
        // on layout hydration later.
        identifyUser(buildAnalyticsUser(response));
        captureUserLoggedIn({ method: 'email', email_domain: response.user?.email?.split('@')[1] || '' });
        const redirect = resolveRedirect();
        // because after login we are not reload site so we have to reload the authority
        reloadAuthorized()
        yield put(routerRedux.replace(redirect || '/'));
      }
    },
    *fetchCurrent(action, { call, put, ...args }) {
      const response = yield call(queryCurrent);
      yield put({
        type: 'saveCurrentUser',
        payload: response,
      });
      // Restored authenticated sessions reach this path after page reloads or
      // token-based bootstrap, so keep a fallback identify here.
      identifyUser(buildAnalyticsUser(response));
    },
    *upgrade(action, { call, put }) {
      const response = yield call();
      yield put({
        type: 'upgradeNotif',
        payload: response,
      });
    },
    *logout(_, { call, put }) {
      // Hit logout endpoint of server
      yield call(logoutFromServer);
      // Reset analytics identity before we clear local auth state.
      captureUserLoggedOut();
      resetPosthog();
      yield put({
        type: 'clearState',
      });
      // redirect
      const { redirect } = getPageQuery();
      if (window.location.pathname !== '/user/login' && !redirect) {
        yield put(
          routerRedux.replace({
            pathname: '/user/login',
            search: stringify({
              redirect: window.location.href,
            }),
          }),
        );
      }
    },
    // inside effects in UserModel (append)
    *socialSignIn({ payload }, { call, put }) {
      // payload: { provider, id_token }
      const resp = yield call(socialLogin, payload.provider, payload.id_token);

      // network error / missing response
      if (!resp) {
        yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
        return;
      }

      // Case A: we use a wrapper { status, body }
      if (typeof resp === 'object' && ('status' in resp)) {
        const { status, body } = resp as any;
        if (!status || status < 200 || status > 299) {
          yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
          return;
        }
        // success
        yield put({ type: 'saveCurrentUser', payload: body });
        if (body && body.token) {
          setToken(body.token);
          // Social login should identify immediately just like password login.
          identifyUser(buildAnalyticsUser(body));
          captureUserLoggedIn({ method: payload.provider === 'microsoft' ? 'microsoft_sso' : payload.provider, email_domain: body.user?.email?.split('@')[1] || '' });
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      // Case B: resp is a fetch Response object
      if (typeof resp === 'object' && typeof (resp as any).ok === 'boolean' && typeof (resp as any).json === 'function') {
        const fetchResp = resp as Response;
        let body = null;
        try {
          body = yield fetchResp.json();
        } catch (err) {
          body = null;
        }
        if (!fetchResp.ok) {
          yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
          return;
        }
        yield put({ type: 'saveCurrentUser', payload: body });
        if (body && body.token) {
          setToken(body.token);
          identifyUser(buildAnalyticsUser(body));
          captureUserLoggedIn({ method: payload.provider === 'microsoft' ? 'microsoft_sso' : payload.provider, email_domain: body.user?.email?.split('@')[1] || '' });
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      // Case C: already parsed JSON object
      if (typeof resp === 'object') {
        if (Object.keys(resp).length === 0) {
          yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
          return;
        }
        yield put({ type: 'saveCurrentUser', payload: resp });
        if ((resp as any).token) {
          setToken((resp as any).token);
          identifyUser(buildAnalyticsUser(resp as any));
          captureUserLoggedIn({ method: payload.provider === 'microsoft' ? 'microsoft_sso' : payload.provider, email_domain: (resp as any).user?.email?.split('@')[1] || '' });
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      // fallback
      yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
    },
    *startResetPassword(_, { put }) {
      yield put({
        type: 'savePasswordResetStatus',
        payload: false,
      });
    },
    *msEmailFirstStart({ payload, callback }, { call, put }) {
      try {
        const res = yield call(microsoftEmailFirstStart, payload);
        if (callback) callback(res);
      } catch (err) {
        if (callback) callback({ error: 'start_failed' });
        yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Unable to start Microsoft SSO'] } });
      }
    },
    // *socialSignInGlobal({ payload }, { call, put }) {
    //   // payload:
    //   // - legacy: { provider, id_token }
    //   // - new Microsoft email-first: { provider:'microsoft', code, state }

    //   let resp: any = null;

    //   // NEW PATH: Microsoft email-first code flow
    //   if (payload.provider === 'microsoft' && payload.code && payload.state) {
    //     resp = yield call(microsoftEmailFirstComplete, { code: payload.code, state: payload.state });
    //   } else {
    //     // OLD PATH: provider token login (google / legacy ms implicit)
    //     resp = yield call(socialLogin, payload.provider, payload.id_token);
    //   }

    //   if (!resp) {
    //     yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
    //     return;
    //   }

    //   // Case A: wrapper { status, body }
    //   if (typeof resp === 'object' && ('status' in resp)) {
    //     const { status, body } = resp as any;
    //     if (!status || status < 200 || status > 299) {
    //       yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
    //       return;
    //     }
    //     yield put({ type: 'saveCurrentUser', payload: body });
    //     if (body && body.token) {
    //       setToken(body.token);
    //       reloadAuthorized();
    //       yield put(routerRedux.replace('/'));
    //     }
    //     return;
    //   }

    //   // Case B: fetch Response
    //   if (typeof resp === 'object' && typeof (resp as any).ok === 'boolean' && typeof (resp as any).json === 'function') {
    //     const fetchResp = resp as Response;
    //     let body = null;
    //     try { body = yield fetchResp.json(); } catch (err) { body = null; }
    //     if (!fetchResp.ok) {
    //       yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
    //       return;
    //     }
    //     yield put({ type: 'saveCurrentUser', payload: body });
    //     if (body && body.token) {
    //       setToken(body.token);
    //       reloadAuthorized();
    //       yield put(routerRedux.replace('/'));
    //     }
    //     return;
    //   }

    //   // Case C: already parsed JSON object
    //   if (typeof resp === 'object') {
    //     if (Object.keys(resp).length === 0) {
    //       yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
    //       return;
    //     }
    //     // If backend returns {non_field_errors:...} for errors:
    //     if ((resp as any).non_field_errors && !(resp as any).token) {
    //       yield put({ type: 'setSignInErrors', payload: resp });
    //       return;
    //     }

    //     yield put({ type: 'saveCurrentUser', payload: resp });
    //     if ((resp as any).token) {
    //       setToken((resp as any).token);
    //       reloadAuthorized();
    //       yield put(routerRedux.replace('/'));
    //     }
    //     return;
    //   }

    //   yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
    // },
    *resetPassword({ payload }, { call, put }) {
      const response = yield call(requestResetPassword, payload);
      if (response.status && response.status !== 200) {
        const res = yield response.json();
        yield put({
          type: 'setPasswordResetErrors',
          payload: res,
        });
        return;
      }
      yield put({
        type: 'savePasswordResetStatus',
        payload: true,
      });
    },
    *confirmresetPassword({ payload }, { call, put }) {
      const response = yield call(requestconfirmResetPassword, payload);

      if (response && response.detail) {
        yield put({
          type: 'savePasswordResetStatus',
          payload: true,
        });
      } else {
        const res = {};
        res.non_field_errors = ['Error in Password Reset, Try Initiating Reset again'];
        yield put({
          type: 'setPasswordResetErrors',
          payload: res,
        });
      }
    },
    *listCart({ payload }, { call, put }) {
      const response = yield call(listCart, payload);
      yield put({
        type: 'cartItems',
        payload: response,
      });
    },
    *listBundles({ payload }, { call, put }) {
      const response = yield call(listBundles, payload);
      yield put({
        type: 'bundleItems',
        payload: response,
      });
    },
    *paymentBundle({ payload }, { call, put }) {
      const response = yield call(paymentUserSubscription, payload);
      yield put({
        type: 'userPackage',
        payload: response,
      });
    },
    *paymentConfig({ payload }, { call, put }) {
      const response = yield call(paymentConfig, payload);
      yield put({
        type: 'getPaymentKey',
        payload: response,
      });
    },
    *getList({ payload }, { call, put }) {
      const response = yield call(getList, payload);
      yield put({
        type: 'saveList',
        payload: response,
      });
    },
    *getProfie({ payload }, { call, put }) {
      const response = yield call(getProfile, payload);
      yield put({
        type: 'saveProfile',
        payload: response,
      });
    },
    *getAllMap({ payload }, { call, put }) {
      const response = yield call(getAllMap, payload);
      yield put({
        type: 'saveAllMap',
        payload: response,
      });
    },
    *fetchsubscriptionStatus({ payload }, { call, put }) {
      const response = yield call(getfetchsubscriptionStatus, payload);
      yield put({
        type: 'savefetchsubscriptionStatus',
        payload: response,
      });
    },
    *updateSubscriptionStatus({ payload }, { call, put }) {
      const response = yield call(updateSubscriptionStatus, payload);
      yield put({
        type: 'savefetchsubscriptionStatus',
        payload: response,
      });
    },
    *fetchSubscriptions(_, { call, put }) {
      yield put({ type: 'setSubsLoading', payload: true });
      try {
        const res = yield call(getfetchsubscriptionStatus);
        yield put({ type: 'saveSubscriptions', payload: Array.isArray(res) ? res : [] });
      } catch (err) {
        notification.error({ message: 'Failed to load subscriptions' });
        yield put({ type: 'saveSubscriptions', payload: [] });
      } finally {
        yield put({ type: 'setSubsLoading', payload: false });
      }
    },
    *updateSubscription({ payload }, { call, put }) {
      try {
        // set the updating flag for UI
        yield put({ type: 'setSubscriptionUpdating', payload: { subscription: payload.subscription, updating: true } });

        const res = yield call(updateSubscriptionStatus, payload);

        if (res) {
          // If backend returns updated single subscription object
          if (res.subscription) {
            yield put({ type: 'updateSingleSubscription', payload: res });
            notification.success({ message: `${res.name || res.subscription} updated` });
          } else if (Array.isArray(res)) {
            // If backend returns full list
            yield put({ type: 'saveSubscriptions', payload: res });
            notification.success({ message: `Subscriptions updated` });
          } else {
            // Unknown response shape - just save to list if array-like
            notification.success({ message: 'Subscription updated' });
          }
        }
      } catch (err) {
        notification.error({ message: 'Failed to update subscription' });
      } finally {
        // always clear updating flag
        yield put({ type: 'setSubscriptionUpdating', payload: { subscription: payload.subscription, updating: false } });
      }
    },
    *fetchNotificationCount(_, { call, put }) {
      const response = yield call(getNotificationCounts);
      yield put({
        type: 'saveNotificationCount',
        payload: response,
      });
    },
    *validateInvitation({ payload, callback }, { call }) {
      try {
        const response = yield call(requestValidateInvitation, payload);
        if (callback) {
          callback(response);
        }
      } catch (err) {
        if (callback) {
          callback({ error: 'Invalid or expired invitation' });
        }
      }
    },
    // models/user.ts or wherever these sagas live
    *ssoEmailFirstStart({ payload, callback }, { call, put }) {
      try {
        const res = yield call(ssoEmailFirstStart, payload);
        if (callback) callback(res);
      } catch (err) {
        if (callback) callback({ error: 'start_failed' });
        yield put({
          type: 'setSignInErrors',
          payload: { non_field_errors: ['Unable to start enterprise SSO'] },
        });
      }
    },
    *socialSignInGlobal({ payload }, { call, put }) {
      // payload:
      // - code flow: { code, state }
      // - legacy token flow: { provider, id_token }

      let resp: any = null;

      // NEW GENERIC CODE FLOW
      if (payload.code && payload.state) {
        resp = yield call(ssoEmailFirstComplete, {
          code: payload.code,
          state: payload.state,
        });
      } else {
        // OLD PATH: google / legacy ms implicit
        resp = yield call(socialLogin, payload.provider, payload.id_token);
      }

      if (!resp) {
        yield put({
          type: 'setSignInErrors',
          payload: { non_field_errors: ['Social login failed'] },
        });
        return;
      }

      if (typeof resp === 'object' && ('status' in resp)) {
        const { status, body } = resp as any;
        if (!status || status < 200 || status > 299) {
          yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
          return;
        }
        yield put({ type: 'saveCurrentUser', payload: body });
        if (body && body.token) {
          setToken(body.token);
          // Code-flow SSO and legacy token flows should share the same
          // immediate identify behavior after successful authentication.
          identifyUser(buildAnalyticsUser(body));
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      if (typeof resp === 'object' && typeof (resp as any).ok === 'boolean' && typeof (resp as any).json === 'function') {
        const fetchResp = resp as Response;
        let body = null;
        try { body = yield fetchResp.json(); } catch (err) { body = null; }
        if (!fetchResp.ok) {
          yield put({ type: 'setSignInErrors', payload: body || { non_field_errors: ['Social login failed'] } });
          return;
        }
        yield put({ type: 'saveCurrentUser', payload: body });
        if (body && body.token) {
          setToken(body.token);
          identifyUser(buildAnalyticsUser(body));
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      if (typeof resp === 'object') {
        if (Object.keys(resp).length === 0) {
          yield put({ type: 'setSignInErrors', payload: { non_field_errors: ['Social login failed'] } });
          return;
        }
        if ((resp as any).non_field_errors && !(resp as any).token) {
          yield put({ type: 'setSignInErrors', payload: resp });
          return;
        }

        yield put({ type: 'saveCurrentUser', payload: resp });
        if ((resp as any).token) {
          setToken((resp as any).token);
          identifyUser(buildAnalyticsUser(resp as any));
          reloadAuthorized();
          yield put(routerRedux.replace(resolveRedirect() || '/'));
        }
        return;
      }

      yield put({
        type: 'setSignInErrors',
        payload: { non_field_errors: ['Social login failed'] },
      });
    },
  },

  reducers: {
    getPaymentKey(state, action) {
      const key = action.payload.public_key;
      utilSetLocalStorage('stripeKey', key);
      return {
        ...state!,
        public_key: key,
      };
    },
    saveCurrentUser(state, action) {
      const { token = state!.token } = action.payload;
      const currentUser = buildAnalyticsUser(action.payload);
      utilSetLocalStorage('User', currentUser.email || '');
      utilSetLocalStorage('UserType', String(currentUser.user_type ?? ''));
      utilSetLocalStorage('UserId', String(currentUser.id ?? ''));
      utilSetLocalStorage('UserGeo', String(currentUser.geo ?? 'none'));
      utilSetLocalStorage('UserDesignation', String(currentUser.designation ?? 'none'));
      utilSetLocalStorage('UserPlan', String(currentUser.plan ?? 'none'));
      // authority includes both user_type and email so email-based route permissions work
      const userType = currentUser.user_type;
      setAuthority([String(userType ?? ''), currentUser.email || ''].filter(Boolean));
      return {
        ...state!,
        token,
        currentUser,
      };
    },
    upgradeNotif(state, action) {
      // send notif for req sent
      notification.info({
        message: 'Upgrade Request sent',
        description: action.payload ? action.payload : '',
      });
      return { ...state! };
    },
    saveStaus(state, action) {
      return {
        ...state!,
        status: action.payload.status,
      };
    },
    savePasswordResetStatus(state, action) {
      return {
        ...state!,
        resetPasswordStatus: action.payload,
      };
    },
    clearState(state) {
      // Preserve atlas_demo_booked across logout — user should not be re-asked to book after booking once
      const demoBooked = localStorage.getItem('atlas_demo_booked');
      utilClearLocalStorage();
      if (demoBooked === 'true') {
        localStorage.setItem('atlas_demo_booked', 'true');
      }
      setToken(null);
      return {
        ...state!,
        status: 'ok',
        resetPasswordStatus: false,
        token: null,
        currentUser: {},
        signInErrors: {},
        signUpErrors: {},
        passwordResetErrors: {},
      };
    },
    setSignInErrors(state, action) {
      return {
        ...state!,
        signInErrors: action.payload,
      };
    },
    setSignUpErrors(state, action) {
      return {
        ...state!,
        signUpErrors: action.payload,
      };
    },
    setPasswordResetErrors(state, action) {
      return {
        ...state!,
        passwordResetErrors: action.payload,
      };
    },
    cartItems(state, action) {
      cartManager(action.payload);
      return {
        ...state!,
        cartList: action.payload,
      };
    },
    bundleItems(state, action) {
      bundlesManager(action.payload);
      return {
        ...state!,
        bundleList: action.payload,
      };
    },
    userPackage(state, action) {
      packageManager(action.payload);
      return {
        ...state!,
        package: action.payload,
      };
    },
    saveList(state, action) {
      return {
        ...state!,
        SEOList: action.payload,
      };
    },
    saveProfile(state, action) {
      return {
        ...state!,
        SEOProfile: action.payload,
      };
    },
    saveAllMap(state, action) {
      return {
        ...state!,
        SEOAllMap: action.payload,
      };
    },
    savefetchsubscriptionStatus(state, action) {
      return {
        ...state!,
        currentUser: {
          ...state!.currentUser,
          is_warning_email_sub: action.payload.is_warning_email_sub,
          is_usfda_crl_email_sub: action.payload.is_usfda_crl_email_sub,
        },
      };
    },
    setSubsLoading(state: UserModelState, { payload }: { payload: boolean }) {
      return { ...state!, subsLoading: payload };
    },
    saveSubscriptions(state: UserModelState, { payload }: { payload: SubscriptionItem[] }) {
      return { ...state!, subscriptions: payload || [] };
    },
    setSubscriptionUpdating(state: UserModelState, { payload }: { payload: { subscription: string; updating: boolean } }) {
      const subs = (state.subscriptions || []).map(s => (s.subscription === payload.subscription ? { ...s, updating: payload.updating } : s));
      return { ...state, subscriptions: subs };
    },
    updateSingleSubscription(state: UserModelState, { payload }: { payload: SubscriptionItem }) {
      const subs = (state.subscriptions || []).slice();
      const idx = subs.findIndex(s => s.subscription === payload.subscription);
      if (idx >= 0) {
        subs[idx] = { ...subs[idx], ...payload, updating: false };
      } else {
        subs.push({ ...payload, updating: false });
      }
      return { ...state, subscriptions: subs };
    },
    saveNotificationCount(state, action) {
      return {
        ...state!,
        notificationCount: action.payload,
      };
    },
  },
}
export default UserModel;
